/**
 * ☰ › Admin › a group › **Sales**: what the group sells and how many hold it.
 * The approved text mock (2026-10-12), drawn:
 *
 *   SALES
 *   How it's sold   (•) Q — QuFolio sells it   ( ) O — the group sells it (later)
 *                   ( ) Not for sale
 *   What it adds    [ One line shown on the card ]
 *   Prices          Monthly   $19.00   ● on sale   [ Stop selling ]
 *                   [ + Add price: (term) $[    ] ]
 *   Buyers          12 · 3 monthly, 8 on a term, 1 forever
 *
 * **Nothing here is stored as a mode.** "How it's sold" is read back from the
 * prices: on sale while any is, otherwise not — the repo's rule against a
 * stored discriminator, and the one place two answers could disagree.
 *
 * Every amount comes from Stripe through the server; the admin types one in
 * major units and the server makes the Stripe price on **the admin's own
 * brand's** account. A new amount for a term already on sale replaces it for
 * new buyers, and whoever already pays the old one keeps paying it.
 */

import { useEffect, useState } from "react";

import {
  addGroupPrice,
  getGroupSales,
  setGroupWords,
  stopGroupPrice,
  type GroupSales as Sales,
} from "../api/admin";
import { SALE_TERMS, formatAmount, termLabel } from "../lib/billing";

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "12px 0 4px",
};

const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };

const GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "110px 1fr",
  gap: "6px 10px",
  alignItems: "center",
};

const LABEL: React.CSSProperties = { color: "var(--qf-text-secondary)" };

const ROW: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };

const INPUT: React.CSSProperties = {
  font: "inherit",
  padding: "2px 6px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
};

const BUTTON: React.CSSProperties = {
  font: "inherit",
  padding: "2px 10px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const DOT: React.CSSProperties = { color: "var(--qf-positive)" };

export interface GroupSalesProps {
  groupKey: string;
  onError: (message: string | null) => void;
}

export function GroupSales({ groupKey, onError }: GroupSalesProps) {
  const [sales, setSales] = useState<Sales | null | undefined>(undefined);
  const [wantQ, setWantQ] = useState(false);
  const [words, setWords] = useState("");
  const [term, setTerm] = useState<string>("once-1y");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setSales(undefined);
    setWantQ(false);
    void getGroupSales(groupKey).then((answer) => {
      if (!alive) return;
      setSales(answer);
      setWords(answer?.description ?? "");
    });
    return () => {
      alive = false;
    };
  }, [groupKey]);

  // Unconfigured, or not this admin's brand: the section is simply absent.
  if (!sales) return null;

  const run = async (write: () => Promise<Sales>) => {
    setBusy(true);
    onError(null);
    try {
      const next = await write();
      setSales(next);
      setWords(next.description);
      return true;
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : String(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSale = sales.prices.length > 0;
  const showQ = onSale || wantQ;
  const { monthly, term: onTerm, forever } = sales.buyers;
  const buyers = monthly + onTerm + forever;

  const stopAll = async () => {
    if (!window.confirm(
      `Stop selling ${sales.name}? Nobody who already bought it loses it.`,
    )) return;
    for (const price of sales.prices) {
      if (!(await run(() => stopGroupPrice(groupKey, price.priceId)))) return;
    }
    setWantQ(false);
  };

  return (
    <>
      <div style={SECTION}>Sales</div>
      <div style={GRID}>
        {sales.tier ? (
          <>
            <span style={LABEL}>How it's sold</span>
            <span style={NOTE}>A tier — sold on the plan screen's cards</span>
          </>
        ) : (
          <>
            <span style={LABEL}>How it's sold</span>
            <span role="radiogroup" aria-label="How it's sold" style={ROW}>
              <label>
                <input
                  type="radio"
                  name={`sold-${groupKey}`}
                  checked={showQ}
                  disabled={busy}
                  onChange={() => setWantQ(true)}
                />{" "}
                Q — QuFolio sells it
              </label>
              <label style={NOTE} title="The group sells on its own Stripe account — not built yet">
                <input type="radio" name={`sold-${groupKey}`} disabled /> O — the group sells it
                (later)
              </label>
              <label>
                <input
                  type="radio"
                  name={`sold-${groupKey}`}
                  checked={!showQ}
                  disabled={busy}
                  onChange={() => (onSale ? void stopAll() : setWantQ(false))}
                />{" "}
                Not for sale
              </label>
            </span>
          </>
        )}

        {showQ && (
          <>
            <span style={LABEL}>What it adds</span>
            <input
              aria-label="What it adds"
              style={INPUT}
              value={words}
              placeholder="One line shown on the card"
              disabled={busy}
              onChange={(event) => setWords(event.target.value)}
              onBlur={() => {
                if (words.trim() !== sales.description) {
                  void run(() => setGroupWords(groupKey, words.trim()));
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") (event.target as HTMLInputElement).blur();
              }}
            />

            <span style={LABEL}>Prices</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {sales.prices.length === 0 && <span style={NOTE}>None on sale yet.</span>}
              {sales.prices.map((price) => (
                <span key={price.priceId} style={ROW}>
                  <span style={{ minWidth: 70 }}>{termLabel(price.interval)}</span>
                  <span style={{ minWidth: 70 }}>{formatAmount(price.amount, price.currency)}</span>
                  <span style={NOTE}>
                    <span style={DOT}>●</span> on sale
                  </span>
                  <button
                    type="button"
                    style={BUTTON}
                    disabled={busy}
                    onClick={() => void run(() => stopGroupPrice(groupKey, price.priceId))}
                  >
                    Stop selling
                  </button>
                </span>
              ))}
              <span style={ROW}>
                <span style={NOTE}>+ Add price:</span>
                <select
                  aria-label="Term"
                  style={INPUT}
                  value={term}
                  disabled={busy}
                  onChange={(event) => setTerm(event.target.value)}
                >
                  {SALE_TERMS.map((value) => (
                    <option key={value} value={value}>
                      {termLabel(value)}
                    </option>
                  ))}
                </select>
                <span>
                  $
                  <input
                    aria-label="Amount"
                    style={{ ...INPUT, width: 80 }}
                    inputMode="decimal"
                    value={amount}
                    disabled={busy}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                </span>
                <button
                  type="button"
                  style={BUTTON}
                  disabled={busy || amount.trim() === ""}
                  onClick={() =>
                    void run(() => addGroupPrice(groupKey, term, amount.trim())).then((ok) => {
                      if (ok) setAmount("");
                    })
                  }
                >
                  Add
                </button>
              </span>
            </span>
          </>
        )}

        <span style={LABEL}>Buyers</span>
        <span style={NOTE}>
          {buyers === 0
            ? "Nobody yet"
            : `${buyers} · ${monthly} monthly, ${onTerm} on a term, ${forever} forever`}
        </span>
      </div>
    </>
  );
}
